import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin, Search, Trash2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const pin = (accent) => L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${accent};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.6)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22],
});

// ricentra solo quando cambia `trigger` (nuova ricerca), NON al trascinamento del pin
const Recenter = ({ pt, trigger }) => {
  const map = useMap();
  useEffect(() => { if (pt) map.setView([pt.lat, pt.lng], Math.max(map.getZoom() || 0, 16)); }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export function AddressDialog({ p, base, params, reload, accent = "#f59e0b", open, onClose }) {
  const [addr, setAddr] = useState(p.indirizzo || "");
  const [pt, setPt] = useState(p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, comune: p.comune, provincia: p.provincia } : null);
  const [trig, setTrig] = useState(0);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const put = (body) => api.put(`${base}/plants/${encodeURIComponent(p.id)}/indirizzo`, body, { params });

  useEffect(() => {
    if (!open) return;
    setAddr(p.indirizzo || "");
    setPt(p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, comune: p.comune, provincia: p.provincia } : null);
    setTrig((t) => t + 1);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const cerca = async () => {
    if (!addr.trim()) return;
    setSearching(true);
    try {
      const r = await put({ indirizzo: addr, preview: true });
      if (!r.data.geo) { toast.error("Indirizzo non trovato: aggiungi via, numero civico e comune"); }
      else { setPt(r.data.geo); setTrig((t) => t + 1); }
    } catch (e) { toast.error(e.response?.data?.detail || "Geolocalizzazione non riuscita"); }
    setSearching(false);
  };

  const salva = async () => {
    if (!pt) return;
    setBusy(true);
    try {
      await put({ indirizzo: addr, lat: pt.lat, lng: pt.lng });
      toast.success("Indirizzo confermato: impianto posizionato sulla mappa");
      reload();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    setBusy(false);
  };

  const rimuovi = async () => {
    setBusy(true);
    try {
      await put({ indirizzo: "" });
      toast.success("Posizione rimossa");
      setPt(null);
      reload();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="plant-address-dialog" aria-describedby="addr-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><MapPin className="h-5 w-5" style={{ color: accent }} />Indirizzo</DialogTitle>
          <DialogDescription id="addr-desc" className="text-xs text-neutral-400">
            Cerca l'indirizzo, controlla il punto sulla mappa e, se serve, trascina il segnalino sul punto esatto prima di confermare.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <input value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && cerca()}
              placeholder="Via, civico, comune (es. Via Sarca 7 Cernusco sul Naviglio)"
              className="h-10 min-w-0 flex-1 rounded-lg border border-neutral-800 bg-black/40 px-3 text-sm text-white outline-none focus:border-neutral-500" data-testid="plant-address-input" />
            <button type="button" onClick={cerca} disabled={searching || !addr.trim()} data-testid="plant-address-search"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg px-3 text-xs font-bold text-black transition-opacity disabled:opacity-40" style={{ background: accent }}>
              {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}Cerca
            </button>
          </div>

          <div className="relative isolate h-[300px] overflow-hidden rounded-xl border border-neutral-800" data-testid="plant-address-map">
            <style>{`.mon-dark-tiles{filter:invert(1) hue-rotate(180deg) brightness(.85) contrast(.9) saturate(.4)}`}</style>
            <MapContainer center={pt ? [pt.lat, pt.lng] : [44.5, 11]} zoom={pt ? 16 : 6} scrollWheelZoom className="h-full w-full" style={{ background: "#0b0b0b" }}>
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" className="mon-dark-tiles" />
              <Recenter pt={pt} trigger={trig} />
              {pt && (
                <Marker position={[pt.lat, pt.lng]} draggable icon={pin(accent)}
                  eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setPt((o) => ({ ...o, lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) })); } }} />
              )}
            </MapContainer>
            {!pt && <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center bg-black/40 px-6 text-center text-sm text-neutral-300" data-testid="plant-address-map-empty">Cerca un indirizzo per vedere il punto sulla mappa</div>}
          </div>

          {pt && (
            <div className="rounded-lg border border-neutral-800 bg-black/30 px-3 py-2 text-xs text-neutral-300" data-testid="plant-address-detected">
              <span className="font-bold text-green-500">Punto rilevato</span>
              {(pt.comune || pt.provincia) && <> · {[pt.comune, pt.provincia && `(${pt.provincia})`].filter(Boolean).join(" ")}</>}
              <span className="ml-1 font-mono text-neutral-500">{pt.lat.toFixed(5)}, {pt.lng.toFixed(5)}</span>
              <span className="mt-0.5 block text-neutral-500">Trascina il segnalino per correggere il punto esatto.</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            {p.posizione_esatta && (
              <button type="button" onClick={rimuovi} disabled={busy} data-testid="plant-address-remove"
                className="inline-flex items-center gap-1 rounded-lg border border-neutral-700 px-3 py-2 text-xs font-bold text-neutral-300 transition-colors hover:border-red-500/60 hover:text-red-400 disabled:opacity-40">
                <Trash2 className="h-3.5 w-3.5" />Rimuovi posizione
              </button>
            )}
            <button type="button" onClick={salva} disabled={busy || !pt} data-testid="plant-address-confirm"
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-bold text-black transition-opacity disabled:opacity-40" style={{ background: accent }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Conferma e salva
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
